import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { realtimeApi, RealtimeBaseline } from "../../services/realtimeApi";

// Night floor: the ambient count a zone shows even when nobody is in it.
//
// Cell-tower catchments are bigger than a polygon, so a zero-population area
// (e.g. Vinterbro Mall) still shows a large count bled in from the homes around
// it (measured there: night floor ≈ 884 vs daytime ≈ 1,670). The median of its
// quiet-hour (02:00–04:59, area-local) samples estimates that background. The
// floor, the median and the reliability threshold are computed server-side by
// GET /realtime/baseline; deducting it is Zone readings' job (deductFloor).

export type NightKey = string; // local calendar date "YYYY-MM-DD"

// Mirrors REALTIME_BASELINE_MIN_QUIET_SAMPLES on the backend — display copy
// only, the actual threshold is enforced server-side (a range under this
// comes back with night_floor: null).
export const MIN_QUIET_SAMPLES = 12;

export interface NightRange {
  from: string;
  to: string;
}

/** The user's Night floor choice: on/off and the nights it is measured over. */
export interface NightFloorSetting {
  enabled: boolean;
  range: NightRange | null; // null = the default nights
}

/**
 * Where the choice is kept. `key` scopes it (null = nothing to scope to, the
 * hook stays idle); a new key restores the choice saved under it.
 */
export interface NightFloorStore {
  key: string | null;
  load(): NightFloorSetting;
  save(setting: NightFloorSetting): void;
}

/** The dashboards' store: off by default, remembered on this device. */
export function localNightFloorStore(key: string | null): NightFloorStore {
  return {
    key,
    load() {
      try {
        const saved = key ? JSON.parse(localStorage.getItem(key) || "null") : null;
        return {
          enabled: !!saved?.enabled,
          range: saved?.fromNight && saved?.toNight ? { from: saved.fromNight, to: saved.toNight } : null,
        };
      } catch {
        return { enabled: false, range: null }; // unreadable preferences
      }
    },
    save({ enabled, range }) {
      if (!key) return;
      try {
        localStorage.setItem(key, JSON.stringify({ enabled, fromNight: range?.from, toNight: range?.to }));
      } catch {
        /* storage unavailable — the view still works, it just won't be remembered */
      }
    },
  };
}

export interface NightFloor {
  /** One entry per zone (only the asked zones when `zoneIds` is given); [] while loading or on failure. */
  baselines: RealtimeBaseline[];
  /** Zones with a usable floor over the chosen nights. A zone with too little night data has none. */
  floorByZone: ReadonlyMap<string, RealtimeBaseline>;
  /** The floor to deduct per zone: empty while the Night floor is off. Hand this to Zone readings. */
  nightFloors: ReadonlyMap<string, number>;
  /** Nights the floor can be measured over, ascending. */
  nights: NightKey[];
  loading: boolean;
  /** The deployment has no /realtime/baseline endpoint (404) — hide the control. */
  unavailable: boolean;
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  /** The nights in use: the chosen ones, else the default (null = the backend's default, all nights). */
  range: NightRange | null;
  setRange: (from: string, to: string) => void;
}

const isAbsent = (err: unknown) => (err as { status?: number })?.status === 404;
const nightsOf = (bs: RealtimeBaseline[]) => Array.from(new Set(bs.flatMap((b) => b.available_nights))).sort();

/**
 * Night floor per zone for a realtime view: fetches the server-computed floors,
 * keeps the user's choice in `store`, and reports when the endpoint isn't deployed.
 * Any other failure leaves the figures raw.
 *
 * - `zoneIds`: the zones to keep (the backend returns every zone the account sees).
 * - `targetId`: narrow the fetch to one zone (the single-area dashboard).
 * - `defaultRange`: pick the default nights from those available. Without it the
 *   default is the backend's own (all nights), fetched in one request.
 */
export function useNightFloor(opts: {
  store: NightFloorStore;
  zoneIds?: string[];
  targetId?: string;
  defaultRange?: (nights: NightKey[]) => NightRange | null;
}): NightFloor {
  const { store, zoneIds, targetId, defaultRange } = opts;
  const key = store.key;
  const storeRef = useRef(store);
  storeRef.current = store;

  const [setting, setSetting] = useState<NightFloorSetting>({ enabled: false, range: null });
  const [fetched, setFetched] = useState<RealtimeBaseline[]>([]);
  const [available, setAvailable] = useState<RealtimeBaseline[]>([]); // defaultRange only: every night
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Restore the saved choice whenever the scope changes.
  useEffect(() => {
    setSetting(key ? storeRef.current.load() : { enabled: false, range: null });
  }, [key]);

  const zoneSet = useMemo(() => (zoneIds ? new Set(zoneIds) : null), [zoneIds]);
  const keep = useCallback(
    (bs: RealtimeBaseline[]) => (zoneSet ? bs.filter((b) => zoneSet.has(String(b.target_id))) : bs),
    [zoneSet]
  );

  // With a default policy, first learn which nights exist (one unranged request).
  const usesDefault = !!defaultRange;
  useEffect(() => {
    if (!key || !usesDefault) {
      setAvailable([]);
      return;
    }
    let cancelled = false;
    realtimeApi
      .getRealtimeBaseline({ targetId })
      .then((b) => !cancelled && setAvailable(b))
      .catch((err) => {
        if (cancelled) return;
        setAvailable([]);
        if (isAbsent(err)) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [key, usesDefault, targetId]);

  const availableNights = useMemo(() => nightsOf(keep(available)), [available, keep]);
  const range = useMemo<NightRange | null>(
    () => setting.range ?? (defaultRange ? defaultRange(availableNights) : null),
    [setting.range, defaultRange, availableNights]
  );
  // A default policy with no eligible nights has nothing to measure.
  const nothingToFetch = usesDefault && !range;
  const rangeFrom = range?.from;
  const rangeTo = range?.to;

  // Fetch the floor. Separate from the chart's own fetch (whose visible range
  // may exclude nights entirely) and deliberately not refreshed by the live
  // poll — the floor is a slow-moving property.
  useEffect(() => {
    if (!key || nothingToFetch) {
      setFetched([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await realtimeApi.getRealtimeBaseline({ targetId, from: rangeFrom, to: rangeTo });
        if (!cancelled) setFetched(data);
      } catch (err) {
        if (!cancelled) {
          setFetched([]); // no floor → figures stay raw
          if (isAbsent(err)) setUnavailable(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key, nothingToFetch, targetId, rangeFrom, rangeTo]);

  const baselines = useMemo(() => keep(fetched), [fetched, keep]);
  const floorByZone = useMemo(() => {
    const m = new Map<string, RealtimeBaseline>();
    baselines.forEach((b) => {
      if (b.night_floor != null) m.set(String(b.target_id), b);
    });
    return m;
  }, [baselines]);
  const nightFloors = useMemo(() => {
    const m = new Map<string, number>();
    if (setting.enabled) floorByZone.forEach((b, id) => m.set(id, b.night_floor as number));
    return m;
  }, [floorByZone, setting.enabled]);
  const nights = useMemo(
    () => (usesDefault ? availableNights : nightsOf(baselines)),
    [usesDefault, availableNights, baselines]
  );

  // Save on USER ACTIONS ONLY. A mount-time effect write races the restore
  // above — state setters land a render later, and StrictMode re-runs the
  // restore after the stale write — which silently clobbers the saved choice.
  const setEnabled = useCallback(
    (on: boolean) => {
      const next = { ...setting, enabled: on };
      setSetting(next);
      storeRef.current.save(next);
    },
    [setting]
  );
  const setRange = useCallback(
    (from: string, to: string) => {
      const next = { ...setting, range: { from, to } };
      setSetting(next);
      storeRef.current.save(next);
    },
    [setting]
  );

  return {
    baselines,
    floorByZone,
    nightFloors,
    nights,
    loading,
    unavailable,
    enabled: setting.enabled,
    setEnabled,
    range,
    setRange,
  };
}
